import type { Auth0ProviderWithConfigOptions } from '@auth0/auth0-react';

import { AUTH0_CONFIG, AUTH0_LOGIN_PATH } from '@/constants/app/config';

/**
 * Callback url Auth0 returns to after Universal Login. Must be listed as an
 * allowed callback and logout url on the tenant's application.
 *
 * @return {*} {string}
 */
export const getAuth0RedirectUri = () => `${window.location.origin}${AUTH0_LOGIN_PATH}`;

export const hasAuthorizationCode = () => new URLSearchParams(window.location.search).has('code');

const initialPath = `${window.location.pathname}${window.location.search}`;

export const getPostLoginPath = () =>
  initialPath.startsWith('/auth/') || initialPath.startsWith('//') ? '/' : initialPath;

/**
 * Props for the Auth0Provider wrapping the v2 auth routes.
 *
 * Refresh tokens are kept in localstorage so the session survives a reload, and
 * the audience is only sent when one is configured — Auth0 issues an opaque
 * token instead of a JWT if the parameter is present but empty.
 */
export const auth0ProviderProps: Omit<Auth0ProviderWithConfigOptions, 'children'> = {
  domain: AUTH0_CONFIG.domain,
  clientId: AUTH0_CONFIG.clientId,
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  cacheLocation: 'localstorage',
  authorizationParams: {
    redirect_uri: getAuth0RedirectUri(),
    ...(AUTH0_CONFIG.audience && { audience: AUTH0_CONFIG.audience }),
    scope: AUTH0_CONFIG.scope,
  },
};

/**
 * Message to surface for a failed Auth0 exchange. The sdk reports some failures
 * as its own error objects and some as plain Errors, so both shapes are read.
 *
 * @return {*} {string}
 */
export const parseAuth0Error = (error: unknown) => {
  const { error_description, message } = (error ?? {}) as {
    error_description?: string;
    message?: string;
  };

  return error_description || message || 'Something went wrong while signing in with Auth0.';
};
